import type { PageTestSource, RunPhase, TestRun } from "@/lib/qa-types";

interface OverviewViewProps {
    runPhase: RunPhase;
    onOpenGenerator: () => void;
    onOpenDefects: () => void;
    source: PageTestSource | null;
    caseCount: number;
    run: TestRun | null;
    runs: TestRun[];
}

const statusPresentation: Record<TestRun["status"], { label: string; tone: string }> = {
    QUEUED: { label: "排队中", tone: "busy" },
    RUNNING: { label: "执行中", tone: "busy" },
    PASSED: { label: "通过", tone: "good" },
    FAILED: { label: "失败", tone: "bad" },
    CANCELLED: { label: "已取消", tone: "bad" },
};

export function OverviewView({ runPhase, onOpenGenerator, onOpenDefects, source, caseCount, run, runs }: OverviewViewProps) {
    const records = uniqueRuns(run, runs);
    const passed = records.filter((item) => item.status === "PASSED").length;
    const failed = records.filter((item) => item.status === "FAILED").length;
    const active = records.filter((item) => item.status === "RUNNING" || item.status === "QUEUED").length;
    const healed = records.filter((item) => item.healing?.decision === "HEALED").length;
    const completed = passed + failed;
    const passRate = completed === 0 ? "—" : `${Math.round((passed / completed) * 1000) / 10}%`;

    return (
        <>
            <section className="hero-row">
                <div>
                    <p className="eyebrow">QUALITY OVERVIEW</p>
                    <h1>质量总览</h1>
                    <p className="hero-copy">
                        {records.length ? `已汇总 ${records.length} 条已保存执行记录。` : "暂无已保存执行记录，请先创建分析任务。"}
                    </p>
                </div>
                <div className="hero-actions">
                    <button className="button primary" onClick={onOpenGenerator}>
                        导入需求
                    </button>
                </div>
            </section>

            {source ? (
                <section className="card pipeline-report-card">
                    <div>
                        <p className="section-kicker">LATEST PAGE TEST REPORT</p>
                        <h2>{source.title}</h2>
                        <a href={source.target_url} rel="noreferrer" target="_blank">
                            {source.target_url}
                        </a>
                        <p>{source.requirement}</p>
                    </div>
                    <div className="pipeline-report-metrics">
                        <span>
                            页面特性 <strong>{source.characteristics.length}</strong>
                        </span>
                        <span>
                            测试场景 <strong>{caseCount}</strong>
                        </span>
                        <span>
                            执行状态 <strong>{run?.status ?? "待执行"}</strong>
                        </span>
                        <span>
                            运行编号 <strong>{run?.run_id ?? "—"}</strong>
                        </span>
                    </div>
                </section>
            ) : (
                <section className="card empty-state">
                    <div>⌁</div>
                    <p>暂无质量报告</p>
                    <span>完成质量探索、场景生成和任务执行后，这里会展示实际分析结果。</span>
                </section>
            )}

            <section className="metrics" aria-label="执行记录指标">
                <Metric delta={`${passed}/${completed} 次通过`} label="执行通过率" tone="cyan" value={passRate} />
                <Metric delta="来自已保存记录" label="执行总数" tone="purple" value={String(records.length)} />
                <Metric delta="来自自愈证据" label="自愈成功" tone="green" value={String(healed)} />
                <Metric delta={failed ? `${failed} 条失败记录` : "暂无失败记录"} label="待处理缺陷" tone="orange" value={String(failed)} />
            </section>

            {run && (
                <section className="workflow-card" aria-live="polite">
                    <div className="workflow-copy">
                        <span className={`pulse-dot ${active ? "active" : ""}`} />
                        <div>
                            <p className="section-kicker">当前执行</p>
                            <strong>{phaseLabel(runPhase)}</strong>
                        </div>
                    </div>
                    <div className="workflow-steps">
                        {[
                            ["环境", run.environment.toUpperCase()],
                            ["优先级", run.priority],
                            ["测试场景", String(run.case_ids.length)],
                            ["执行 Agent", run.agent_id],
                        ].map(([label, value]) => (
                            <div className="workflow-step current" key={label}>
                                <span>{value}</span>
                                <small>{label}</small>
                            </div>
                        ))}
                    </div>
                    {run.healing && (
                        <div className="healing-result">
                            <span className="result-mark">{run.healing.decision === "HEALED" ? "✓" : "!"}</span>
                            <div>
                                <strong>{healingLabel(run.healing.decision)}</strong>
                                <p>{run.run_id}</p>
                            </div>
                            <div className="score-pair">
                                <span>视觉 {run.healing.visual_score.toFixed(2)}</span>
                                <span>语义 {run.healing.semantic_score.toFixed(2)}</span>
                            </div>
                        </div>
                    )}
                </section>
            )}

            <section className="card runs-card">
                <CardHead kicker="持久化记录" title="最近执行" action="全部执行记录" onAction={onOpenDefects} />
                {records.length === 0 ? (
                    <div className="empty-state">
                        <div>▶</div>
                        <p>暂无执行记录</p>
                        <span>任务执行完成后，记录会自动保存并显示在这里。</span>
                    </div>
                ) : (
                    <div className="run-table">
                        {records.slice(0, 5).map((item) => {
                            const presentation = statusPresentation[item.status];
                            return (
                                <article className="run-row" key={item.run_id}>
                                    <div className="run-icon">▶</div>
                                    <div className="run-name">
                                        <strong>{item.requirement || "未填写执行需求"}</strong>
                                        <span>{item.run_id}</span>
                                    </div>
                                    <span className={`status-pill ${presentation.tone}`}>{presentation.label}</span>
                                    <span className="run-detail">
                                        {item.environment.toUpperCase()} · {item.case_ids.length} 条场景
                                    </span>
                                    {item.source_url ? (
                                        <a
                                            aria-label={`打开 ${item.run_id} 来源页面`}
                                            href={item.source_url}
                                            rel="noreferrer"
                                            target="_blank"
                                        >
                                            ↗
                                        </a>
                                    ) : (
                                        <span>—</span>
                                    )}
                                </article>
                            );
                        })}
                    </div>
                )}
            </section>
        </>
    );
}

function uniqueRuns(current: TestRun | null, persisted: TestRun[]): TestRun[] {
    const records = current ? [current, ...persisted] : persisted;
    return records.filter((item, index) => records.findIndex((candidate) => candidate.run_id === item.run_id) === index);
}

function Metric({ label, value, delta, tone }: { label: string; value: string; delta: string; tone: string }) {
    return (
        <article className={`metric ${tone}`}>
            <div className="metric-icon" />
            <p>{label}</p>
            <strong>{value}</strong>
            <span>{delta}</span>
        </article>
    );
}

function CardHead({ kicker, title, action, onAction }: { kicker: string; title: string; action: string; onAction: () => void }) {
    return (
        <header className="card-head">
            <div>
                <p>{kicker}</p>
                <h2>{title}</h2>
            </div>
            <button onClick={onAction}>
                {action} <span>→</span>
            </button>
        </header>
    );
}

function phaseLabel(phase: RunPhase): string {
    return { idle: "尚未执行", queued: "等待执行", running: "执行中", healing: "自愈处理中", passed: "执行完成" }[phase];
}

function healingLabel(decision: NonNullable<TestRun["healing"]>["decision"]): string {
    return { HEALED: "自愈成功", REJECTED: "自愈未通过", NOT_NEEDED: "无需自愈" }[decision];
}
